import React, { memo } from 'react'
import { H2 } from '../../../../../Heading/index'
import EyeHide from '../../../../../Icon/icons/EyeHide/index'
import Plus from '../../../../../Icon/icons/Plus/index'
import StackedCards from '../../../../../Icon/icons/StackedCards/index'
import Button, { TYPES as BUTTON_TYPES, SIZES as BUTTON_SIZES } from '../../../../../Button/index'

/**
 * Desktop header block of `PlaylistTabContainer`.
 *
 * Scope: the title + sharing-count badge + desktop "Create Playlist"
 * button only — i.e. `__sticky-controls` child #1. The mobile
 * "Create Playlist" button (child #4, class
 * `playlist-tab-container__create-btn--mobile`) stays in the
 * container as a SIBLING of this component, NOT nested inside —
 * moving it would break sticky positioning and the
 * `__create-btn--mobile` CSS targeting.
 *
 * Translation strings arrive as already-resolved props so the
 * static-text boundary stays at the container.
 */

// Memoize the icon constants so a fresh element isn't allocated on every
// render. Both icons sit in the same ternary at the render site (`count > 0
// ? StackedCardsIcon : EyeHideIcon`), so memoise both for symmetry.
const StackedCardsIcon = <StackedCards />
const EyeHideIcon = <EyeHide />

export interface PlaylistTabsHeaderProps {
  collectionsTitle: string
  /** Template like `'Sharing on {count} profiles'` — interpolated when count > 0. */
  sharingCountTemplate: string
  sharedOnProfileCount?: number
  allPrivateLabel: string
  createPlaylistLabel: string
  showCreate: boolean
  onCreateClick: () => void
  onCreatePreload: () => void
}

function PlaylistTabsHeaderBase ({
  collectionsTitle,
  sharingCountTemplate,
  sharedOnProfileCount,
  allPrivateLabel,
  createPlaylistLabel,
  showCreate,
  onCreateClick,
  onCreatePreload,
}: PlaylistTabsHeaderProps) {
  return (
    <div className="playlist-tab-container__header">
      <div className="playlist-tab-container__header-left">
        <H2 className="playlist-tab-container__title">
          {collectionsTitle}
        </H2>
        {sharedOnProfileCount != null ? (
          <span className="playlist-tab-container__privacy-summary">
            {sharedOnProfileCount > 0 ? StackedCardsIcon : EyeHideIcon}
            {sharedOnProfileCount > 0
              ? sharingCountTemplate.replace('{count}', String(sharedOnProfileCount))
              : allPrivateLabel}
          </span>
        ) : null}
      </div>
      {showCreate ? (
        <Button
          type={BUTTON_TYPES.PRIMARY}
          size={BUTTON_SIZES.DEFAULT}
          className="playlist-tab-container__create-btn"
          onClick={onCreateClick}
          onMouseEnter={onCreatePreload}
        >
          <Plus />
          {createPlaylistLabel}
        </Button>
      ) : null}
    </div>
  )
}

const PlaylistTabsHeader = memo(PlaylistTabsHeaderBase)
PlaylistTabsHeader.displayName = 'PlaylistTabsHeader'
export default PlaylistTabsHeader
